/** Presentational pieces shared by the Home, History, Setup and Settings screens. */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PerSide, Teams } from '@/core';
import { LABEL } from './format';

/** The Hoopscope logo mark (same artwork as resources/icon.svg), drawn in theme colours. */
export function LogoMark({ className = 'h-[30px] w-[30px]' }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true" className={`flex-none ${className}`}>
      <g stroke="rgb(var(--hs-accent))" strokeWidth="3.2" strokeLinecap="round" fill="none">
        <circle cx="32" cy="32" r="28" />
        <path d="M32 1v9M32 54v9M1 32h9M54 32h9" />
      </g>
      <circle cx="32" cy="32" r="17" fill="rgb(var(--hs-accent))" />
      <path
        d="M15 32h34M32 15v34M20.5 20c5 4 5 20 0 24M43.5 20c-5 4-5 20 0 24"
        fill="none"
        stroke="rgb(var(--hs-bg))"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function BallIcon({ className = 'h-[30px] w-[30px]' }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={`flex-none ${className}`}>
      <circle cx="16" cy="16" r="14" fill="rgb(var(--hs-accent))" />
      <path
        d="M2 16h28M16 2v28M6 6c5 4 5 16 0 20M26 6c-5 4-5 16 0 20"
        fill="none"
        stroke="rgb(var(--hs-accent-ink))"
        strokeWidth="1.6"
      />
    </svg>
  );
}

export function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6">
      <path
        d="M15 5l-7 7 7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 flex-none text-muted">
      <path
        d="M9 5l7 7-7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <circle cx="12" cy="12" r="4.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Round team-colour dot. Colour is runtime data (team choice), not a design token. */
export function TeamDot({ color, className = 'h-3 w-3' }: { color?: string; className?: string }) {
  return (
    <i
      aria-hidden="true"
      className={`inline-block flex-none rounded-full shadow-[inset_0_0_0_1px_rgb(var(--hs-fg)/0.25)] ${className}`}
      style={{ background: color ?? 'rgb(var(--hs-muted))' }}
    />
  );
}

/** Two-line team / score block used on cards and in lists. */
export function MiniScore({
  teams,
  score,
  size = 'lg',
}: {
  teams: Teams;
  score: PerSide<number>;
  size?: 'lg' | 'sm';
}) {
  const sides = ['home', 'away'] as const;
  return (
    <div className="grid gap-1">
      {sides.map((side) => {
        const other = side === 'home' ? 'away' : 'home';
        const behind = score[side] < score[other];
        return (
          <div key={side} className="flex min-w-0 items-center gap-2.5">
            <TeamDot color={teams[side].color} />
            <span
              className={`min-w-0 flex-1 truncate font-semibold ${size === 'lg' ? 'text-[17px]' : 'text-[15px]'}`}
            >
              {teams[side].name}
            </span>
            <b
              className={`tabular font-display font-bold leading-[1.05] ${
                size === 'lg' ? 'text-4xl' : 'text-2xl'
              } ${behind ? 'text-muted' : 'text-fg'}`}
            >
              {score[side]}
            </b>
          </div>
        );
      })}
    </div>
  );
}

/** Top bar for inner screens: back link, title, optional trailing content. */
export function ScreenHeader({
  title,
  backTo,
  children,
}: {
  title: ReactNode;
  backTo: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="flex min-h-touch flex-wrap items-center gap-2">
      <Link
        to={backTo}
        aria-label={t('common.back')}
        className="-ml-3 inline-flex min-h-touch min-w-touch items-center justify-center rounded-full text-fg"
      >
        <BackIcon />
      </Link>
      <h1
        className={`min-w-0 flex-1 break-words ${children === undefined ? '' : 'max-[420px]:order-last max-[420px]:basis-full'} font-display text-2xl leading-tight max-[420px]:text-xl font-bold uppercase tracking-wide [html[lang=ta]_&]:font-tamil [html[lang=ta]_&]:text-xl [html[lang=ta]_&]:normal-case [html[lang=ta]_&]:tracking-normal`}
      >
        {title}
      </h1>
      {children !== undefined && <div className="ml-auto flex-none">{children}</div>}
    </header>
  );
}

export function SectionLabel({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <h2 id={id} className={LABEL}>
      {children}
    </h2>
  );
}
